import {
    translationRequestKey,
    type ReaderTranslation,
    type ReaderTranslationRequest,
} from './translationRequests';

type FetchTranslations = (requests: ReaderTranslationRequest[]) => Promise<ReaderTranslation[]>;
export interface TranslationState {
    translation: ReaderTranslation | null;
    busy: boolean;
    unavailable: boolean;
}
interface Entry {
    request: ReaderTranslationRequest;
    original: string;
    state: TranslationState;
    checked: boolean;
    requested: boolean;
    polls: number;
    listeners: Set<(state: TranslationState) => void>;
}

// Keep recently displayed translations when a section closes, without retaining
// every piece of text visited through pagination during a long page visit.
const MAX_UNUSED_TRANSLATIONS = 200;

/** One owner for a page's read batches, explicit generation, and pending-result timer. */
export const createPageTranslations = (read: FetchTranslations, generate: FetchTranslations) => {
    const entries = new Map<string, Entry>();
    let enabled = false;
    let active = true;
    let scheduled = false;
    let running = false;
    let rerun = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const publish = (entry: Entry, state: TranslationState) => {
        entry.state = state;
        entry.listeners.forEach((listener) => listener(state));
    };
    const current = (entry: Entry) =>
        active && entries.get(translationRequestKey(entry.request, entry.original)) === entry;
    const retainCompleted = (key: string, entry: Entry) => {
        if (entry.state.translation?.status !== 'COMPLETED') {
            entries.delete(key);
            return;
        }
        // Map order records the most recently released translation last.
        entries.delete(key);
        entries.set(key, entry);
        const unused = [...entries].filter(([, value]) => !value.listeners.size);
        for (const [expired] of unused.slice(0, -MAX_UNUSED_TRANSLATIONS)) {
            entries.delete(expired);
        }
    };

    const fetchBatches = async (batch: Entry[], generating: boolean) => {
        for (let start = 0; active && start < batch.length; start += 25) {
            const chunk = batch.slice(start, start + 25).filter(current);
            if (!chunk.length) continue;
            for (const entry of chunk) {
                if (generating) entry.requested = true;
                publish(entry, { ...entry.state, busy: true, unavailable: false });
            }
            let results: ReaderTranslation[] = [];
            try {
                results = await (generating ? generate : read)(
                    chunk.map((entry) => ({
                        ...entry.request,
                        ...(generating && entry.state.translation?.status === 'FAILED'
                            ? { retryFailed: true }
                            : {}),
                    })),
                );
            } catch {
                // Keep the original text and expose an explicit retry.
            }
            for (const entry of chunk) {
                if (!current(entry)) continue;
                entry.checked = true;
                const result = results.find(
                    (value) =>
                        translationRequestKey(value, value.originalText) ===
                        translationRequestKey(entry.request, entry.original),
                );
                publish(entry, {
                    translation: result ?? null,
                    busy: Boolean(enabled && result?.status === 'PENDING' && entry.polls < 20),
                    unavailable: !result || result.status === 'FAILED',
                });
            }
        }
    };

    const run = async () => {
        scheduled = false;
        if (!active) return;
        if (running) {
            rerun = true;
            return;
        }
        running = true;
        clearTimeout(timer);
        try {
            await fetchBatches(
                [...entries.values()].filter((entry) => !entry.checked),
                false,
            );
            if (active && enabled) {
                await fetchBatches(
                    [...entries.values()].filter(
                        (entry) =>
                            !entry.requested &&
                            ['MISSING', 'FAILED'].includes(entry.state.translation?.status ?? ''),
                    ),
                    true,
                );
            }
        } finally {
            running = false;
        }
        if (!active) return;
        const pending = [...entries.values()].filter(
            (entry) => enabled && entry.state.translation?.status === 'PENDING' && entry.polls < 20,
        );
        if (pending.length) {
            timer = setTimeout(() => {
                for (const entry of pending.filter(current)) {
                    entry.polls += 1;
                    entry.checked = false;
                }
                schedule();
            }, 1500);
        }
        if (rerun) {
            rerun = false;
            schedule();
        }
    };
    const schedule = () => {
        if (!active || scheduled) return;
        scheduled = true;
        queueMicrotask(() => void run());
    };

    return {
        register(
            request: ReaderTranslationRequest,
            original: string,
            listener: (state: TranslationState) => void,
        ) {
            const key = translationRequestKey(request, original);
            let entry = entries.get(key);
            if (!entry) {
                entry = {
                    request,
                    original,
                    state: { translation: null, busy: true, unavailable: false },
                    checked: false,
                    requested: false,
                    polls: 0,
                    listeners: new Set(),
                };
                entries.set(key, entry);
            }
            entry.listeners.add(listener);
            listener(entry.state);
            schedule();
            return () => {
                entry.listeners.delete(listener);
                if (!entry.listeners.size && entries.get(key) === entry)
                    retainCompleted(key, entry);
            };
        },
        enable() {
            enabled = true;
            for (const entry of entries.values()) {
                if (entry.state.unavailable || entry.polls >= 20) {
                    entry.checked = false;
                    entry.requested = false;
                    entry.polls = 0;
                }
                if (entry.state.translation?.status === 'PENDING') {
                    publish(entry, { ...entry.state, busy: true });
                }
            }
            schedule();
        },
        activate() {
            active = true;
            schedule();
        },
        dispose() {
            active = false;
            clearTimeout(timer);
            entries.clear();
        },
    };
};
